/**
 * ПРИБОР «ГЛАЗАМИ РЕКТОРА» — договор маяка вкладок админки (программа «Пульт», строка 1.1, 2 окт 2026;
 * журнал docs/audit/2026-10-01-panel-command-center/MASTER.md).
 *
 * Зачем. За месяц ректор тринадцать раз жаловался на панель («ждать минуту», «не открывается»), и ни одну
 * жалобу не поймал прибор: прошлые круги ускорения мерили сервер — время ручек, вес чанков, — а не то, что
 * видит человек. Здесь мерило — время от нажатия на вкладку (вход — от начала загрузки страницы) до метки
 * «данные видны», которую ставит сам экран, и куда это время ушло: сеть, наш шлюз, браузер.
 *
 * Одна правда на обе стороны (устав П9): панель строит маяк этими типами, шлюз панели разбирает его этой же
 * функцией и решает исход по горячим бюджетам (`panel-view-tuning.ts`). Имена полей маяка и вкладок —
 * отсюда, рассинхрон невозможен по построению.
 *
 * Время раскладывается без остатка: `ms = net + server + browser`.
 *   wait    — пока хоть что-то из сети было в пути (запросы данных, чанки, на входе — сама страница);
 *   server  — из него наш шлюз думал над ответом (заголовок Server-Timing ответа); не прочитан — null;
 *   net     — wait − server: дорога до нас и обратно, очередь, вес;
 *   browser — ms − wait: наш код в браузере — разбор чанков, отрисовка, анимации, паузы.
 */
/** Сколько самое большее ждём метку: дольше — исход «завис» (`stuck`), маяк уходит, не дожидаясь ухода. */
export const PANEL_VIEW_MAX_MS = 120_000;
/** Вкладки и экраны админки: метка прибора → путь и человеческое название. Новый экран — новая строка. */
export const PANEL_VIEWS = [
    { name: 'nerve', title: 'Нервный центр (главная)', path: /^\/$/ },
    { name: 'logs', title: 'Логи', path: /^\/logs\/?$/ },
    { name: 'chats', title: 'Чаты: список', path: /^\/viewer\/?$/ },
    { name: 'chat', title: 'Чаты: чат', path: /^\/viewer\/chat\/[^/]+(\/.*)?$/ },
    { name: 'people', title: 'Чаты: люди', path: /^\/viewer\/people\/?$/ },
    { name: 'person', title: 'Чаты: человек', path: /^\/viewer\/people\/[^/]+\/?$/ },
    { name: 'facts', title: 'Чаты: факты', path: /^\/viewer\/facts\/?$/ },
    { name: 'fact', title: 'Чаты: факт', path: /^\/viewer\/facts\/[^/]+\/?$/ },
    { name: 'graph', title: 'Чаты: граф', path: /^\/viewer\/graph\/?$/ },
    { name: 'billing', title: 'Биллинг', path: /^\/billing\/?$/ },
    { name: 'economics', title: 'Ведомость (P&L)', path: /^\/economics\/?$/ },
    { name: 'prompts', title: 'Промпты', path: /^\/prompts\/?$/ },
    { name: 'gather', title: 'Добор', path: /^\/gather\/?$/ },
    { name: 'studios', title: 'Студии', path: /^\/studios-admin\/?$/ },
    { name: 'refs', title: 'Рефералы', path: /^\/referrals\/?$/ },
    { name: 'devices', title: 'Устройства (вне меню)', path: /^\/devices\/?$/ },
    { name: 'body', title: 'Что это было (вне меню)', path: /^\/body\/?$/ },
    { name: 'devlog', title: 'DevLog', path: /^\/devlog\/?$/ },
    { name: 'security', title: 'Безопасность', path: /^\/security\/?$/ },
    { name: 'graph3', title: 'Граф V3', path: /^\/graph-v3\/?$/ },
    { name: 'pp', title: 'Рекламные вставки', path: /^\/pp\/?$/ },
    { name: 'rupor', title: 'Выкладка', path: /^\/rupor\/?$/ },
    { name: 'dashboard', title: 'Сводка (вне меню)', path: /^\/dashboard\/?$/ },
    { name: 'observatory', title: 'ЦУП (вне меню)', path: /^\/observatory\/?$/ },
    { name: 'dev', title: 'Витрина деталей чата (вне меню)', path: /^\/dev\/chat-primitives\/?$/ },
];
const VIEW_NAMES = new Set(PANEL_VIEWS.map((v) => v.name));
/** Вкладка по адресу страницы; null — не экран (переадресация `/chats`, чужой путь). */
export function panelViewOf(pathname) {
    for (const v of PANEL_VIEWS)
        if (v.path.test(pathname))
            return v.name;
    return null;
}
/** Человеческое название вкладки. */
export function panelViewTitle(name) {
    return PANEL_VIEWS.find((v) => v.name === name)?.title ?? name;
}
const KINDS = new Set(['entry', 'switch']);
const ENDS = new Set(['ready', 'failed', 'left', 'stuck']);
const DEVICES = new Set([
    'windows',
    'android',
    'ios',
    'mac',
    'linux',
    'other',
]);
const HOSTS = new Set(['panel', 'ru.panel', 'go', 'other']);
const BRIDGES = new Set(['ru', 'ams']);
const ECT = /^(slow-2g|2g|3g|4g)$/;
const UNREAD_WHY = new Set(['header', 'start']);
const oneOf = (v, allowed) => typeof v === 'string' && allowed.has(v) ? v : null;
/** Число мс в [0, потолок]; не число — null. */
function msOf(v, max = PANEL_VIEW_MAX_MS) {
    return typeof v === 'number' && Number.isFinite(v) && v >= 0
        ? Math.min(Math.round(v), max)
        : null;
}
function countOf(v, max) {
    return typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.min(Math.round(v), max) : 0;
}
function pathOf(v) {
    return typeof v === 'string' && v.length > 0 ? v.slice(0, 120) : null;
}
function statusOf(v) {
    return typeof v === 'number' && Number.isInteger(v) && v >= 100 && v <= 599 ? v : null;
}
/**
 * Разбор маяка с панели: белые списки и потолки (метки Прометея и строка журнала не принимают произвольное),
 * раскладка сведена к `server ≤ wait ≤ ms`. Не вкладка, не вид или не исход — null (маяк отбрасывается).
 */
export function parsePanelViewBeacon(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const r = raw;
    const view = oneOf(r.view, VIEW_NAMES);
    const kind = oneOf(r.kind, KINDS);
    const end = oneOf(r.end, ENDS);
    const ms = msOf(r.ms);
    if (!view || !kind || !end || ms === null)
        return null;
    const waitMs = Math.min(msOf(r.waitMs) ?? 0, ms);
    const server = msOf(r.serverMs);
    const serverMs = server === null ? null : Math.min(server, waitMs);
    const s = r.slowest;
    const slowestPath = s && typeof s === 'object' ? pathOf(s.path) : null;
    const bad = Array.isArray(r.bad)
        ? r.bad.slice(0, 5).flatMap((b) => {
            const bb = b;
            const path = bb ? pathOf(bb.path) : null;
            const status = bb ? statusOf(bb.status) : null;
            return path && status ? [{ path, status }] : [];
        })
        : [];
    const unread = Array.isArray(r.unread)
        ? r.unread.slice(0, 5).flatMap((u) => {
            const uu = u;
            const path = uu ? pathOf(uu.path) : null;
            const why = uu ? oneOf(uu.why, UNREAD_WHY) : null;
            return path && why ? [{ path, why }] : [];
        })
        : [];
    const at = typeof r.at === 'number' && Number.isFinite(r.at) && r.at > 0 ? Math.round(r.at) : 0;
    return {
        view,
        kind,
        end,
        ms,
        waitMs,
        serverMs,
        staticMs: Math.min(msOf(r.staticMs) ?? 0, waitMs),
        bytes: countOf(r.bytes, 1e9),
        reqs: countOf(r.reqs, 10_000),
        cached: countOf(r.cached, 10_000),
        slowest: s && typeof s === 'object' && slowestPath
            ? {
                path: slowestPath,
                ms: msOf(s.ms) ?? 0,
                serverMs: msOf(s.serverMs),
                bytes: countOf(s.bytes, 1e9),
                status: statusOf(s.status),
            }
            : null,
        bad,
        unread,
        marks: countOf(r.marks, 100),
        hidden: r.hidden === true,
        login: r.login === true,
        device: oneOf(r.device, DEVICES) ?? 'other',
        tg: r.tg === true,
        host: oneOf(r.host, HOSTS) ?? 'other',
        bridge: oneOf(r.bridge, BRIDGES),
        edge: r.edge === 'other' ? 'other' : 'own',
        rtt: msOf(r.rtt, 60_000),
        ect: typeof r.ect === 'string' && ECT.test(r.ect) ? r.ect : null,
        at,
    };
}
/** Раскладка времени без остатка: сеть + наш шлюз + браузер = ms. Шлюз не прочитан — сеть и шлюз вместе. */
export function panelViewSplit(b) {
    return {
        netMs: b.serverMs === null ? null : b.waitMs - b.serverMs,
        serverMs: b.serverMs,
        browserMs: b.ms - b.waitMs,
    };
}
export const PANEL_VIEW_OUTCOMES = [
    'ok',
    'slow',
    'failed',
    'stuck',
    'left',
    'hidden',
    'login',
];
export function panelViewOutcome(b, budgets) {
    const budgetMs = b.kind === 'entry' ? budgets.entry_budget_ms : budgets.switch_budget_ms;
    if (b.end === 'failed')
        return { outcome: 'failed', budgetMs };
    if (b.hidden)
        return { outcome: 'hidden', budgetMs };
    if (b.login)
        return { outcome: 'login', budgetMs };
    if (b.end === 'stuck')
        return { outcome: 'stuck', budgetMs };
    if (b.ms > budgetMs)
        return { outcome: 'slow', budgetMs };
    return { outcome: b.end === 'left' ? 'left' : 'ok', budgetMs };
}
/** Устройство — по платформе браузера (`navigator.userAgentData.platform`), без неё — по строке браузера. */
export function panelDeviceOf(platform, ua) {
    const p = (platform ?? '').toLowerCase();
    if (p === 'windows')
        return 'windows';
    if (p === 'android')
        return 'android';
    if (p === 'ios')
        return 'ios';
    if (p === 'macos')
        return 'mac';
    if (p === 'linux' || p === 'chrome os' || p === 'chromeos')
        return 'linux';
    if (/Android/.test(ua))
        return 'android';
    if (/iPhone|iPad|iPod/.test(ua))
        return 'ios';
    if (/Windows/.test(ua))
        return 'windows';
    if (/Mac OS X|Macintosh/.test(ua))
        return 'mac';
    if (/Linux|CrOS/.test(ua))
        return 'linux';
    return 'other';
}
/** Каким адресом открыта панель. Личные имена умного входа (`<код>.go`) — одним словом `go`. */
export function panelHostOf(hostname) {
    if (hostname === 'panel.padla.pro')
        return 'panel';
    if (hostname === 'ru.panel.padla.pro')
        return 'ru.panel';
    if (hostname === 'go.padla.pro' || hostname.endsWith('.go.padla.pro'))
        return 'go';
    return 'other';
}
/** Путь ручки для журнала: без адреса и строки запроса, без префикса `/api`, числа и длинные ключи — `:n`. */
export function panelApiPathOf(url) {
    let path = url;
    try {
        path = new URL(url, 'https://panel.padla.pro').pathname;
    }
    catch {
        path = url.split('?')[0] ?? url;
    }
    return path
        .replace(/^\/api(?=\/)/, '')
        .split('/')
        .map((seg) => (/^-?\d+$/.test(seg) || /^[0-9a-f-]{16,}$/i.test(seg) ? ':n' : seg))
        .join('/')
        .slice(0, 120);
}
//# sourceMappingURL=panel-view.js.map